import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { McProfile, useGamingStore } from '../../../store/gamingStore';
import { Save, Trash2, AlertTriangle, Loader2, ChevronDown, Search } from 'lucide-react';

interface Props {
  profile: McProfile;
}

type McVersionEntry = { id: string; type: string; releaseTime: string };

const LOADERS = [
  { id: 'vanilla', label: 'Vanilla', color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
  { id: 'forge',   label: 'Forge',   color: 'text-orange-400 border-orange-500/30 bg-orange-500/10' },
  { id: 'fabric',  label: 'Fabric',  color: 'text-blue-400 border-blue-500/30 bg-blue-500/10' },
  { id: 'quilt',   label: 'Quilt',   color: 'text-purple-400 border-purple-500/30 bg-purple-500/10' },
] as const;

const ProfileSettingsTab: React.FC<Props> = ({ profile }) => {
  const { updateProfile, deleteProfile, closeProfile } = useGamingStore();

  const [name, setName] = useState(profile.name);
  const [serverAddress, setServerAddress] = useState(profile.server?.address || '');
  const [serverPort, setServerPort] = useState(String(profile.server?.port || 25565));
  const [autoConnect, setAutoConnect] = useState(profile.server?.auto_connect ?? true);
  const [minMemory, setMinMemory] = useState(String(profile.jvm?.min_memory || ''));
  const [maxMemory, setMaxMemory] = useState(String(profile.jvm?.max_memory || ''));
  const [javaPath, setJavaPath] = useState(profile.jvm?.java_path || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  // --- Minecraft config ---
  const [mcVersion, setMcVersion] = useState(profile.minecraft?.version || '1.20.4');
  const [loaderType, setLoaderType] = useState(profile.minecraft?.loader || 'vanilla');
  const [loaderVersion, setLoaderVersion] = useState(profile.minecraft?.loader_version || '');

  // --- MC version dropdown ---
  const [mcVersions, setMcVersions] = useState<McVersionEntry[]>([]);
  const [loadingMcVersions, setLoadingMcVersions] = useState(true);
  const [mcVersionFilter, setMcVersionFilter] = useState<'release' | 'snapshot' | 'all'>('release');
  const [mcVersionSearch, setMcVersionSearch] = useState('');
  const [mcVersionDropdownOpen, setMcVersionDropdownOpen] = useState(false);
  const mcTriggerRef = React.useRef<HTMLButtonElement>(null);
  const mcDropdownRef = React.useRef<HTMLDivElement>(null);
  const [mcDropdownPos, setMcDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const api = (window as any).api;
    if (api?.getMinecraftVersions) {
      api.getMinecraftVersions().then((data: McVersionEntry[]) => {
        setMcVersions(Array.isArray(data) ? data : []);
      }).catch(() => {}).finally(() => setLoadingMcVersions(false));
    } else {
      setLoadingMcVersions(false);
    }
  }, []);

  useEffect(() => {
    if (mcVersionDropdownOpen && mcTriggerRef.current) {
      const rect = mcTriggerRef.current.getBoundingClientRect();
      setMcDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [mcVersionDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        mcTriggerRef.current && !mcTriggerRef.current.contains(target) &&
        mcDropdownRef.current && !mcDropdownRef.current.contains(target)
      ) {
        setMcVersionDropdownOpen(false);
      }
    };
    if (mcVersionDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mcVersionDropdownOpen]);

  const filteredMcVersions = mcVersions.filter((v) => {
    if (mcVersionFilter !== 'all' && v.type !== mcVersionFilter) return false;
    if (mcVersionSearch && !v.id.toLowerCase().includes(mcVersionSearch.toLowerCase())) return false;
    return true;
  });

  // --- Loader versions ---

  type LoaderVersionEntry = { version: string; label: string; recommended?: boolean; latest?: boolean; stable?: boolean };
  const [loaderVersions, setLoaderVersions] = useState<LoaderVersionEntry[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  useEffect(() => {
    if (loaderType === 'vanilla' || loaderType === 'quilt') {
      setLoaderVersions([]);
      setVersionsError(null);
      return;
    }
    setLoadingVersions(true);
    setVersionsError(null);
    const api = (window as any).api;

    const doFetch = async () => {
      try {
        if (loaderType === 'forge') {
          const result = await api?.getForgeVersions?.(mcVersion);
          if (!result?.success) throw new Error(result?.error || 'Échec de récupération');
          // Sort: recommended first, then latest, then rest (newest first as returned by XMCL)
          const sorted: LoaderVersionEntry[] = (result.versions as any[])
            .map(v => ({
              version: v.forgeVersion,
              label: v.forgeVersion,
              recommended: !!v.recommended,
              latest: !!v.latest,
            }))
            .sort((a, b) => {
              if (a.recommended && !b.recommended) return -1;
              if (!a.recommended && b.recommended) return 1;
              if (a.latest && !b.latest) return -1;
              if (!a.latest && b.latest) return 1;
              return 0;
            });
          setLoaderVersions(sorted);
          // Auto-select recommended, or latest, or first
          const best = sorted.find(v => v.recommended) || sorted.find(v => v.latest) || sorted[0];
          if (best && !sorted.find(v => v.version === loaderVersion)) {
            setLoaderVersion(best.version);
          }
        } else if (loaderType === 'fabric') {
          const result = await api?.getFabricVersions?.(mcVersion);
          if (!result?.success) throw new Error(result?.error || 'Échec de récupération');
          // Sort: stable first
          const sorted: LoaderVersionEntry[] = (result.versions as any[])
            .map(v => ({
              version: v.loaderVersion,
              label: v.loaderVersion,
              stable: !!v.stable,
            }))
            .sort((a, b) => {
              if (a.stable && !b.stable) return -1;
              if (!a.stable && b.stable) return 1;
              return 0;
            });
          setLoaderVersions(sorted);
          const best = sorted.find(v => v.stable) || sorted[0];
          if (best && !sorted.find(v => v.version === loaderVersion)) {
            setLoaderVersion(best.version);
          }
        }
      } catch (err: any) {
        setVersionsError(err?.message || 'Erreur réseau');
        setLoaderVersions([]);
      } finally {
        setLoadingVersions(false);
      }
    };

    doFetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaderType, mcVersion]);

  const handleSave = async () => {
    setIsSaving(true);
    setSavedOk(false);
    await updateProfile(profile.id, {
      name,
      minecraft: {
        version: mcVersion,
        loader: loaderType,
        loader_version: (loaderType !== 'vanilla' && loaderVersion) ? loaderVersion : null,
      },
      server: {
        address: serverAddress,
        port: parseInt(serverPort) || 25565,
        auto_connect: autoConnect,
      },
      jvm: {
        min_memory: minMemory ? parseInt(minMemory) : null,
        max_memory: maxMemory ? parseInt(maxMemory) : null,
        extra_args: profile.jvm?.extra_args || [],
        java_path: javaPath || null,
      },
    });
    setIsSaving(false);
    setSavedOk(true);
    setTimeout(() => setSavedOk(false), 2000);
  };

  const handleDelete = async () => {
    await deleteProfile(profile.id);
    closeProfile();
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
      {/* General */}
      <section className="bg-bg-secondary border border-border-color rounded-xl p-4 space-y-4">
        <h3 className="text-sm font-semibold text-text-primary">Général</h3>
        <div>
          <label className="text-xs text-text-muted block mb-1">Nom du profil</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary focus:border-gaming-accent outline-none"
          />
        </div>
      </section>

      {/* Minecraft */}
      <section className="bg-bg-secondary border border-border-color rounded-xl p-4 space-y-4">
        <h3 className="text-sm font-semibold text-text-primary">Minecraft</h3>

        {/* Version */}
        <div>
          <label className="text-xs text-text-muted block mb-1.5">Version Minecraft</label>
          <div className="relative">
            <button
              ref={mcTriggerRef}
              type="button"
              onClick={() => setMcVersionDropdownOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary hover:border-gaming-accent/50 focus:border-gaming-accent outline-none transition-colors"
            >
              <span>{mcVersion || 'Sélectionner une version'}</span>
              {loadingMcVersions ? (
                <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
              ) : (
                <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${mcVersionDropdownOpen ? 'rotate-180' : ''}`} />
              )}
            </button>

            {mcVersionDropdownOpen && mcDropdownPos && ReactDOM.createPortal(
              <div
                ref={mcDropdownRef}
                className="fixed z-[100] bg-bg-secondary border border-border-color rounded-lg shadow-xl overflow-hidden"
                style={{ top: mcDropdownPos.top, left: mcDropdownPos.left, width: mcDropdownPos.width }}
              >
                {/* Search + filter */}
                <div className="p-2 border-b border-border-color space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
                    <input
                      type="text"
                      value={mcVersionSearch}
                      onChange={(e) => setMcVersionSearch(e.target.value)}
                      placeholder="Rechercher..."
                      autoFocus
                      className="w-full pl-8 pr-3 py-1.5 bg-bg-primary border border-border-color rounded-md text-xs text-text-primary placeholder:text-text-muted focus:border-gaming-accent outline-none"
                    />
                  </div>
                  <div className="flex gap-1">
                    {(['release', 'snapshot', 'all'] as const).map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setMcVersionFilter(f)}
                        className={`flex-1 py-1 rounded-md text-[10px] font-medium transition-colors ${
                          mcVersionFilter === f
                            ? 'bg-gaming-accent/15 text-gaming-accent border border-gaming-accent/30'
                            : 'bg-bg-primary text-text-muted border border-border-color hover:border-gaming-accent/20'
                        }`}
                      >
                        {f === 'release' ? 'Release' : f === 'snapshot' ? 'Snapshot' : 'Toutes'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Version list */}
                <div className="max-h-72 overflow-y-auto gaming-scrollbar">
                  {filteredMcVersions.length === 0 ? (
                    <p className="text-xs text-text-muted text-center py-4">Aucune version trouvée</p>
                  ) : (
                    filteredMcVersions.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => { setMcVersion(v.id); setMcVersionDropdownOpen(false); setMcVersionSearch(''); }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-xs transition-colors ${
                          mcVersion === v.id
                            ? 'bg-gaming-accent/10 text-gaming-accent'
                            : 'text-text-primary hover:bg-bg-tertiary'
                        }`}
                      >
                        <span className="font-medium">{v.id}</span>
                        <span className={`text-[10px] ${v.type === 'release' ? 'text-emerald-400' : 'text-text-muted'}`}>
                          {v.type === 'release' ? 'Release' : v.type === 'snapshot' ? 'Snapshot' : v.type}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>,
              document.body
            )}
          </div>
        </div>

        {/* Loader */}
        <div>
          <label className="text-xs text-text-muted block mb-1.5">Mod loader</label>
          <div className="grid grid-cols-4 gap-2">
            {LOADERS.map(l => (
              <button
                key={l.id}
                onClick={() => { setLoaderType(l.id); setLoaderVersion(''); }}
                className={`py-2 rounded-lg border text-xs font-medium transition-all ${
                  loaderType === l.id ? l.color : 'border-border-color bg-bg-primary text-text-secondary hover:border-gaming-accent/30'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loader version */}
        {loaderType !== 'vanilla' && (
          <div>
            <label className="text-xs text-text-muted block mb-1.5 flex items-center gap-1.5">
              Version {loaderType === 'forge' ? 'Forge' : loaderType === 'fabric' ? 'Fabric' : 'Quilt'}
              {loadingVersions && <Loader2 className="w-3 h-3 animate-spin text-text-muted" />}
            </label>

            {versionsError && (
              <p className="text-xs text-red-400 mb-1.5">⚠ {versionsError} — saisissez la version manuellement</p>
            )}

            {loaderVersions.length > 0 ? (
              <>
                <select
                  value={loaderVersion}
                  onChange={(e) => setLoaderVersion(e.target.value)}
                  className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary focus:border-gaming-accent outline-none"
                >
                  <option value="">-- Choisir une version --</option>
                  {loaderVersions.map(v => (
                    <option key={v.version} value={v.version}>
                      {v.version}
                      {v.recommended ? '  ⭐ Recommandée' : v.latest ? '  🔥 Latest' : v.stable === false ? '  ⚠ Bêta' : ''}
                    </option>
                  ))}
                </select>
                {/* Badge pill for selected version */}
                {(() => {
                  const sel = loaderVersions.find(v => v.version === loaderVersion);
                  if (!sel) return null;
                  if (sel.recommended) return <p className="text-[10px] text-emerald-400 mt-1">⭐ Version recommandée (stable et testée)</p>;
                  if (sel.latest) return <p className="text-[10px] text-orange-400 mt-1">🔥 Dernière version disponible</p>;
                  if (sel.stable === false) return <p className="text-[10px] text-yellow-400 mt-1">⚠ Version bêta — instable</p>;
                  return null;
                })()}
              </>
            ) : !loadingVersions ? (
              <input
                type="text"
                value={loaderVersion}
                onChange={(e) => setLoaderVersion(e.target.value)}
                placeholder={loaderType === 'forge' ? 'ex: 47.2.0' : 'ex: 0.16.9'}
                className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary focus:border-gaming-accent outline-none"
              />
            ) : (
              <div className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-muted">
                Chargement des versions...
              </div>
            )}
          </div>
        )}
      </section>

      {/* Server */}
      <section className="bg-bg-secondary border border-border-color rounded-xl p-4 space-y-4">
        <h3 className="text-sm font-semibold text-text-primary">Serveur</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-text-muted block mb-1">Adresse</label>
            <input
              type="text"
              value={serverAddress}
              onChange={(e) => setServerAddress(e.target.value)}
              placeholder="play.example.com"
              className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary focus:border-gaming-accent outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-text-muted block mb-1">Port</label>
            <input
              type="number"
              value={serverPort}
              onChange={(e) => setServerPort(e.target.value)}
              className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary focus:border-gaming-accent outline-none"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoConnect}
            onChange={(e) => setAutoConnect(e.target.checked)}
            className="accent-gaming-accent w-4 h-4"
          />
          <span className="text-xs text-text-secondary">Se connecter automatiquement au serveur au lancement</span>
        </label>
      </section>

      {/* JVM */}
      <section className="bg-bg-secondary border border-border-color rounded-xl p-4 space-y-4">
        <h3 className="text-sm font-semibold text-text-primary">JVM (optionnel)</h3>
        <p className="text-xs text-text-muted">Laissez vide pour utiliser les paramètres globaux.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-text-muted block mb-1">Mémoire min (Mo)</label>
            <input
              type="number"
              value={minMemory}
              onChange={(e) => setMinMemory(e.target.value)}
              placeholder="1024"
              className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary focus:border-gaming-accent outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-text-muted block mb-1">Mémoire max (Mo)</label>
            <input
              type="number"
              value={maxMemory}
              onChange={(e) => setMaxMemory(e.target.value)}
              placeholder="2048"
              className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary focus:border-gaming-accent outline-none"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-text-muted block mb-1">Chemin Java</label>
          <input
            type="text"
            value={javaPath}
            onChange={(e) => setJavaPath(e.target.value)}
            placeholder="java (défaut système)"
            className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary focus:border-gaming-accent outline-none"
          />
        </div>
      </section>

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={isSaving}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gaming-accent text-white text-sm font-medium hover:bg-gaming-accent-hover transition-colors disabled:opacity-60"
      >
        {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {savedOk ? 'Sauvegardé ✓' : isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
      </button>

      {/* Danger zone */}
      <section className="bg-bg-secondary border border-red-500/20 rounded-xl p-4 space-y-3">
        <h3 className="text-sm font-semibold text-red-400 flex items-center gap-1">
          <AlertTriangle className="w-4 h-4" />
          Zone de danger
        </h3>
        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-500/30 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Supprimer ce profil
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-400">Confirmer la suppression ?</span>
            <button
              onClick={handleDelete}
              className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors"
            >
              Supprimer
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-1.5 rounded-lg border border-border-color text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              Annuler
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ProfileSettingsTab;
